/* eslint-disable react/jsx-props-no-spreading */
import { AiPromptResponse, GetConversation } from '@/client/services/api/graphql/gql/graphql';
import { Box, ButtonGroup, IconButton, Textarea } from '@chakra-ui/react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ArrowRight } from '../../icons/ContinuIcons';
import { getPromptResponseQuery } from '../api/getPromptResponseQuery';
import { graphQLClient } from '@/client/services/api/clients/graphqlClient';
import { useAIAgentStore } from './AIAgentStore';
import { useToastStore } from '@/client/services/state/toastStore';
import { useTranslation } from 'react-i18next';

type AiFormValues = {
  prompt: string;
};

export default function AIAgentQueryInput() {
  const { t } = useTranslation();
  const { setToast } = useToastStore();
  const {
    currentConversationId,
    setCurrentConversationId,
    setIsConversationActive,
    previewExpert,
  } = useAIAgentStore();
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { isValid },
  } = useForm<AiFormValues>();

  const { prompt } = watch();

  const getResponseFromPrompt = async (
    query: string,
    channel: string,
    conversationId?: string,
  ): Promise<AiPromptResponse> => {
    setIsConversationActive(true);

    // If no conversation ID (new conversation), create placeholder query and query data
    if (!currentConversationId) {
      setCurrentConversationId('INITIAL_CONVERSATION');

      queryClient.setQueryData(
        ['conversation', 'INITIAL_CONVERSATION'],
        [
          {
            __typename: 'GetConversation' as const,
            channel: 'in_app',
            conversation: 'INITIAL_CONVERSATION',
            createdAt: new Date().toISOString(),
            id: `PROCESSING_RESPONSE`,
            query,
            references: [],
            response: t('aiAgent.workingOnIt'),
            sessionId: 'INITIAL_CONVERSATION',
            threadId: 'INITIAL_CONVERSATION',
          } as GetConversation,
        ],
      );
    } else {
      // For existing conversations, add processing response to query cache
      queryClient.setQueryData(
        ['conversation', currentConversationId],
        (oldData: GetConversation[] | undefined) =>
          oldData
            ? [
                ...oldData,
                {
                  __typename: 'GetConversation' as const,
                  channel: 'in_app',
                  conversation: currentConversationId || '',
                  createdAt: new Date().toISOString(),
                  id: `PROCESSING_RESPONSE`,
                  query,
                  references: [],
                  response: t('aiAgent.workingOnIt'),
                  sessionId: currentConversationId || '',
                  threadId: currentConversationId || '',
                } as GetConversation,
              ]
            : oldData,
      );
    }

    reset();

    const data: any = await graphQLClient.request(getPromptResponseQuery, {
      prompt: query,
      channel,
      conversationId,
      expertId: previewExpert?.id,
      inPreview: !!previewExpert,
    });

    return data.getPromptResponse;
  };

  const getResponseFromPromptMutation = useMutation({
    mutationFn: () => getResponseFromPrompt(prompt, 'in_app', currentConversationId),
    onSuccess: (data) => {
      if (currentConversationId === 'INITIAL_CONVERSATION') {
        setCurrentConversationId(data.conversationId);
      }

      queryClient.refetchQueries({ queryKey: ['conversation', currentConversationId] });

      queryClient.invalidateQueries({ queryKey: ['ai-agent-conversations'] });

      reset();
    },
    onError: () => {
      setToast({
        show: true,
        status: 'error',
        title: t('aiAgent.prompt.error'),
      });
    },
  });

  const { status } = getResponseFromPromptMutation;

  const onSubmit: SubmitHandler<AiFormValues> = () => getResponseFromPromptMutation.mutateAsync();

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ flexShrink: 0, marginTop: 'auto' }}>
      <Box position="relative" backgroundColor="white">
        <Textarea
          {...register('prompt', { required: true })}
          placeholder={`${t('aiAgent.prompt.placeholder')}`}
          resize="none"
          fontSize="14px"
          fontWeight={500}
          color="baseBlack"
          rows={5}
          backgroundColor="white"
          border="1px solid"
          borderColor="neutral.100"
          focusBorderColor="transparent"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSubmit(onSubmit)();
            }
          }}
          _placeholder={{ fontSize: '14px', fontWeight: 500, color: 'neutral.600' }}
          _focus={{
            borderColor: 'neutral.200',
          }}
        />

        <ButtonGroup zIndex={9999} position="absolute" right={4} bottom={4} size="xs">
          <IconButton
            variant="AIAgent"
            type="submit"
            isDisabled={!isValid}
            aria-label={t('aiAgent.askAi')}
            width="fit-content"
            icon={<ArrowRight />}
            isLoading={status === 'loading'}
          />
        </ButtonGroup>
      </Box>
    </form>
  );
}
